import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { useStory } from '../../context/StoryContext';
import * as XLSX from 'xlsx';
import ChronicleModal from './ChronicleModal';
import EncyclopediaModal from './EncyclopediaModal';
import BookmarksPanel from './BookmarksPanel';

const ArchivePanel = ({ isSidebar = false }) => {
  const { state, archiveCurrentStory, startOverStory, importStoryState, unarchiveChapter, toggleChapterBookmark } = useStory();
  const [search, setSearch] = useState('');
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [viewingChronicle, setViewingChronicle] = useState(null);
  const [showEncyclopedia, setShowEncyclopedia] = useState(false);

  const canExport = state.subscriptionTier === 'Storyteller' || state.subscriptionTier === 'Worldbuilder';
  const hasEncyclopediaAccess = (state.encyclopedia && state.encyclopedia.length > 0) || state.subscriptionTier === 'Storyteller' || state.subscriptionTier === 'Worldbuilder';

  const prepareTabularData = () => {
    const data = [];

    // Flatten current active story
    state.story.forEach(entry => {
      data.push({
        Chapter: 'Current Active Session',
        Role: entry.role,
        Content: entry.content,
        Timestamp: new Date(entry.timestamp).toLocaleString()
      });
    });

    // Flatten archived chapters
    state.archive.forEach(chapter => {
      chapter.content.forEach(entry => {
         data.push({
            Chapter: chapter.title,
            Role: entry.role,
            Content: entry.content,
            Timestamp: new Date(entry.timestamp || chapter.timestamp).toLocaleString()
         });
      });
    });
    return data;
  };

  const handleExport = (format) => {
    if (!canExport) {
        alert("Story Export & Cloud Sync is available on the Storyteller tier and above. Please upgrade to export your story.");
        return;
    }

    const timestamp = new Date().getTime();

    if (format === 'json') {
      const jsonStr = JSON.stringify(state);
      const blob = new Blob([jsonStr], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const downloadAnchorNode = document.createElement('a');
      downloadAnchorNode.setAttribute("href", url);
      downloadAnchorNode.setAttribute("download", `weaver_story_export_${timestamp}.json`);
      document.body.appendChild(downloadAnchorNode);
      downloadAnchorNode.click();
      downloadAnchorNode.remove();
      URL.revokeObjectURL(url);
    } else {
      const data = prepareTabularData();
      const worksheet = XLSX.utils.json_to_sheet(data);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Story Archive");

      if (format === 'csv') {
        XLSX.writeFile(workbook, `weaver_story_export_${timestamp}.csv`);
      } else if (format === 'xlsx') {
        XLSX.writeFile(workbook, `weaver_story_export_${timestamp}.xlsx`);
      }
    }
    setShowExportMenu(false);
  };

  const handleImport = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = async (event) => {
        try {
          const importedState = JSON.parse(event.target.result);
          if (window.confirm('Are you sure you want to import this story? It will be added to your current progress.')) {
            await importStoryState(importedState);
          }
        } catch (error) {
          if (window.confirm('Are you sure you want to import this text document? It will be added to your current progress.')) {
            const fallbackState = {
              story: [{ role: 'user', content: event.target.result, timestamp: new Date().toISOString() }]
            };
            await importStoryState(fallbackState);
          }
        }
      };
      reader.readAsText(file);
    }
  };

  const filteredArchive = state.archive.filter(ch => 
    ch.title.toLowerCase().includes(search.toLowerCase()) || 
    ch.content.some(e => e.content.toLowerCase().includes(search.toLowerCase()))
  );

  return (
    <div className="space-y-6">
      {isSidebar && (
        <div className="mb-4 space-y-4">
          <button
            onClick={() => hasEncyclopediaAccess ? setShowEncyclopedia(true) : alert('World Encyclopedia is available on the Storyteller tier and above. Please upgrade to use this feature.')}
            className="w-full py-2 bg-indigo-50 dark:bg-indigo-900/20 text-indigo-700 dark:text-indigo-300 hover:bg-indigo-100 dark:hover:bg-indigo-900/40 rounded-xl text-xs font-bold transition-colors flex items-center justify-center space-x-2 border border-indigo-200 dark:border-indigo-800"
          >
            <SafeIcon icon={!hasEncyclopediaAccess ? FiIcons.FiLock : FiIcons.FiBook} />
            <span>World Encyclopedia</span>
          </button>

          <div className="pt-4 border-t border-gray-100 dark:border-purple-800/50">
            <BookmarksPanel />
          </div>
        </div>
      )}

      <div className="flex justify-between items-center">
        <h4 className="text-[10px] font-black text-gray-900 dark:text-purple-400 uppercase tracking-widest flex items-center space-x-2">
          <SafeIcon icon={FiIcons.FiBookOpen} className="text-amber-500" />
          <span>Chapters</span>
        </h4>

        {!isSidebar && (
          <div className="flex space-x-1 relative">
            <label className={`p-1.5 rounded-lg transition-colors cursor-pointer ${!canExport ? 'bg-gray-100 text-gray-400 opacity-50' : 'bg-gray-500/10 text-gray-900 hover:bg-gray-500/20'}`} title="Import Story">
              <SafeIcon icon={!canExport ? FiIcons.FiLock : FiIcons.FiUpload} />
              <input type="file" accept=".json,.txt,.csv,application/json,text/plain,text/csv" onChange={handleImport} className="hidden" disabled={!canExport} />
            </label>

            <div className="relative">
              <button
                onClick={() => canExport ? setShowExportMenu(!showExportMenu) : alert("Story Export & Cloud Sync is available on the Storyteller tier and above. Please upgrade to export your story.")}
                className={`p-1.5 rounded-lg transition-colors ${!canExport ? 'bg-gray-100 text-gray-400 opacity-50 cursor-not-allowed' : 'bg-gray-500/10 text-gray-900 hover:bg-gray-500/20'}`}
                title="Export Story"
              >
                <SafeIcon icon={!canExport ? FiIcons.FiLock : FiIcons.FiDownload} />
              </button>

              <AnimatePresence>
                {showExportMenu && (
                  <motion.div
                    initial={{ opacity: 0, y: 5 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 5 }}
                    className="absolute right-0 top-full mt-1 w-32 bg-white dark:bg-purple-950 border border-gray-200 dark:border-purple-800 rounded-xl shadow-lg overflow-hidden z-10"
                  >
                    <button onClick={() => handleExport('json')} className="w-full text-left px-4 py-2 text-xs text-gray-900 dark:text-purple-300 hover:bg-gray-50 dark:hover:bg-purple-900 transition-colors">Export JSON</button>
                    <button onClick={() => handleExport('csv')} className="w-full text-left px-4 py-2 text-xs text-gray-900 dark:text-purple-300 hover:bg-gray-50 dark:hover:bg-purple-900 transition-colors border-t border-gray-100 dark:border-purple-800/50">Export CSV</button>
                    <button onClick={() => handleExport('xlsx')} className="w-full text-left px-4 py-2 text-xs text-gray-900 dark:text-purple-300 hover:bg-gray-50 dark:hover:bg-purple-900 transition-colors border-t border-gray-100 dark:border-purple-800/50">Export Excel</button>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
        )}
      </div>

      {isSidebar && (
        <>
          <div className="relative">
            <input
              value={search} onChange={e => setSearch(e.target.value)}
              className="input-field text-xs py-2 pl-8" placeholder="Search chronicles..."
            />
            <SafeIcon icon={FiIcons.FiSearch} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-900 text-sm dark:text-purple-300" />
          </div>

          <div className="space-y-2">
            {filteredArchive.map((ch) => (
              <div key={ch.id} className="group">
                <button
                  onClick={() => {
                      const event = new CustomEvent('view-archive-chapter', { detail: { chapter: ch } });
                      window.dispatchEvent(event);
                  }}
                  className="w-full p-3 rounded-xl border text-left transition-all bg-white dark:bg-purple-900/40 border-gray-100 dark:border-purple-800 hover:border-purple-300 relative group"
                >
                  <div className="flex justify-between items-center">
                    <span className="text-xs font-bold text-gray-900 dark:text-white truncate flex items-center gap-1">
                      {ch.bookmarked && <SafeIcon icon={FiIcons.FiBookmark} className="text-amber-500 fill-current text-[10px]" />}
                      {ch.title}
                    </span>

                    <div className="flex space-x-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <div
                        onClick={(e) => { e.stopPropagation(); toggleChapterBookmark(ch.id, !ch.bookmarked); }}
                        className={`p-1 rounded ${ch.bookmarked ? 'text-amber-500 bg-amber-50 dark:bg-amber-900/50' : 'bg-gray-50 dark:bg-purple-800/50 text-gray-900 dark:text-purple-300 hover:text-amber-600 dark:hover:text-amber-500 hover:bg-amber-50 dark:hover:bg-amber-900/50'}`}
                        title={ch.bookmarked ? "Remove Bookmark" : "Bookmark Chapter"}
                      >
                         <SafeIcon icon={FiIcons.FiBookmark} className={ch.bookmarked ? 'fill-current text-[10px]' : 'text-[10px]'} />
                      </div>
                      <div
                        onClick={(e) => { e.stopPropagation(); unarchiveChapter(ch.id); }}
                        className="p-1 rounded bg-gray-50 dark:bg-purple-800/50 text-gray-900 dark:text-purple-300 hover:text-amber-600 dark:hover:text-amber-500 hover:bg-amber-50 dark:hover:bg-amber-900/50"
                        title="Unarchive Chapter"
                      >
                         <SafeIcon icon={FiIcons.FiRotateCcw} className="text-[10px]" />
                      </div>
                    </div>
                  </div>
                  <span className="text-[9px] text-gray-900 dark:text-purple-400 mt-1 block">{new Date(ch.timestamp).toLocaleDateString()} • {ch.content.length} entries</span>
                </button>
              </div>
            ))}
            {filteredArchive.length === 0 && (
              <div className="text-center py-8">
                <SafeIcon icon={FiIcons.FiInbox} className="text-2xl text-gray-900 mx-auto mb-2 dark:text-purple-200" />
                <p className="text-[10px] text-gray-900 dark:text-purple-400">No chapters found</p>
              </div>
            )}
          </div>
        </>
      )}

      {!isSidebar && (
        <div className="pt-6 border-t border-gray-100 dark:border-purple-800">
           <button
             onClick={() => {
               if (window.confirm('Are you absolutely sure you want to delete your entire story, including all archives? This cannot be undone.')) {
                 startOverStory();
               }
             }}
             className="w-full py-2 bg-red-50 dark:bg-red-900/10 text-red-500 hover:bg-red-100 dark:hover:bg-red-900/30 rounded-xl text-xs font-bold transition-colors flex items-center justify-center space-x-2"
           >
             <SafeIcon icon={FiIcons.FiTrash2} />
             <span>Delete Entire Story</span>
           </button>
        </div>
      )}

      <ChronicleModal
        chapter={viewingChronicle}
        onClose={() => setViewingChronicle(null)}
      />

      {showEncyclopedia && (
        <EncyclopediaModal onClose={() => setShowEncyclopedia(false)} />
      )}
    </div>
  );
};

export default ArchivePanel;